/**
 * hooks/useTemplatePreviewData.ts
 *
 * Hook to load and transform template data for preview mode.
 * Encapsulates all data loading, transformation, and ordering for client experience.
 *
 * Updated Feb 2026: Removed screeningQuestions from return value. Conditions are
 *   now handled inline via stepSequenceBuilder in ClientCollect.
 * Updated Feb 2026: Removed selected_subtypes and quantity from needs mapping.
 * Updated Mar 2026: Removed legacy condition_catalog loading. Now uses new
 *   `conditions` JSONB column with parseConditionsFromDb fallback.
 * Updated Apr 2026 (single logo source of truth): query now joins users to resolve
 *   logo_url from the form owner's profile. Returns userLogoUrl for the preview modal.
 * Updated Apr 2026: added viewerUserId param - logo is now resolved from the viewer's
 *   profile, not the template owner's. This fixes assigned templates showing no logo.
 * Updated Apr 2026: logo fetch moved into the same Promise.all as fields/documents
 *   so all three queries run in parallel.
 * Updated Jun 2026: removed form_template_documents query (table dropped). All field
 *   types including 'document' now come from template_fields.
 */

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { NeedItem } from '../types/needs';
import type { FormTemplate } from '../types/database';
import { groupNeedsBySection, orderNeedsForClient } from '../utils/needsTransformer';
import { parseConditionsFromDb } from '../utils/jsonHelpers';
import { safeParseOptions } from '../utils/jsonHelpers';
import { captureError } from '../utils/errorReporting';

interface UseTemplatePreviewDataResult {
  template: FormTemplate | null;
  userLogoUrl: string | null;
  orderedNeeds: NeedItem[];
  loading: boolean;
  error: string | null;
}

export function useTemplatePreviewData(
  templateId: string | null,
  viewerUserId?: string | null
): UseTemplatePreviewDataResult {
  const [template, setTemplate] = useState<FormTemplate | null>(null);
  const [userLogoUrl, setUserLogoUrl] = useState<string | null>(null);
  const [orderedNeeds, setOrderedNeeds] = useState<NeedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!templateId) {
      setTemplate(null);
      setUserLogoUrl(null);
      setOrderedNeeds([]);
      setLoading(false);
      setError(null);
      return;
    }

    const loadTemplateData = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data: templateData, error: templateError } = await supabase
          .from('form_templates')
          .select('*')
          .eq('id', templateId)
          .maybeSingle();

        if (templateError) throw templateError;
        if (!templateData) {
          setError('Formulaire introuvable');
          setLoading(false);
          return;
        }

        setTemplate(templateData as FormTemplate);

        const logoUserId = viewerUserId ?? templateData.user_id;

        const [fieldsResult, logoResult] = await Promise.all([
          supabase
            .from('template_fields')
            .select('*')
            .eq('template_id', templateId)
            .order('position'),
          logoUserId
            ? supabase.from('users').select('logo_url').eq('id', logoUserId).maybeSingle()
            : Promise.resolve({ data: null, error: null }),
        ]);

        if (fieldsResult.error) throw fieldsResult.error;

        setUserLogoUrl(logoResult.data?.logo_url ?? null);

        const fields = fieldsResult.data || [];

        const needs: NeedItem[] = fields.map((field) => ({
          id: field.id,
          type: field.type,
          label: field.label,
          short_label: field.short_label ?? null,
          description: field.description,
          is_required: field.is_required,
          position: field.position,
          options: field.options ? safeParseOptions(field.options) : undefined,
          conditions: parseConditionsFromDb(
            field.conditions,
            null,
            null
          ),
          section: field.type === 'document' ? null : (field.section ?? null),
          is_system: field.is_system ?? false,
        }));

        const grouped = groupNeedsBySection(needs);
        const ordered = orderNeedsForClient(grouped);
        setOrderedNeeds(ordered);
        setLoading(false);
      } catch (err) {
        console.error('Error loading template preview data:', err);
        captureError(err, { hook: 'useTemplatePreviewData', action: 'loadTemplateData', templateId });
        setError("Erreur lors du chargement de l'aperçu");
        setLoading(false);
      }
    };

    loadTemplateData();
  }, [templateId, viewerUserId]);

  return { template, userLogoUrl, orderedNeeds, loading, error };
}
